import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Toaster, toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, Users, CheckCircle, XCircle, AlertCircle,
  Mail, Download, Trash2, Eye, Send, UserPlus, Percent, GraduationCap, Calendar
} from 'lucide-react';
import Navbar from '../components/Navbar';
import DateOfBirthInput from '../components/DateOfBirthInput';
import api, { studentsAPI, classesAPI } from '../lib/api';

const StudentImport = () => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Pending invites state
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [sendingBulkInvites, setSendingBulkInvites] = useState(false);
  const [selectedPending, setSelectedPending] = useState([]);
  
  // Add student modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [addingStudent, setAddingStudent] = useState(false);

  // "Add as sibling" retry prompt — shown when the backend rejects because a
  // pending registration already exists for this parent email.
  const [siblingPrompt, setSiblingPrompt] = useState(null); // { existingStudentName, submitData }
  const [existingParentPrompt, setExistingParentPrompt] = useState(null); // { parentName }
  
  // Support for multiple students (siblings)
  const emptyStudent = {
    student_name: '',
    birth_date: '',
    gender: '',
    medical_conditions: '',
    no_medical_conditions: false,
    class_ids: [],
    discount_percent: 0,
    discount_note: '',
    discount_one_time: false,
    mailing_lists: []
  };
  
  const [students, setStudents] = useState([{ ...emptyStudent }]);
  const [addingSiblings, setAddingSiblings] = useState(false);
  const [parentInfo, setParentInfo] = useState({
    parent_email: '',
    parent_first_name: '',
    parent_last_name: '',
    parent_phone: '',
    // Second guardian (for separated parents)
    guardian2_email: '',
    guardian2_first_name: '',
    guardian2_last_name: '',
    guardian2_phone: '',
    guardian2_receives_emails: true,
    send_invite: true
  });
  
  // Legacy single student state (for backwards compatibility)
  const [newStudent, setNewStudent] = useState({
    student_name: '',
    parent_email: '',
    parent_first_name: '',
    parent_last_name: '',
    parent_phone: '',
    // Second guardian (for separated parents)
    guardian2_email: '',
    guardian2_first_name: '',
    guardian2_last_name: '',
    guardian2_phone: '',
    guardian2_receives_emails: true,
    // Student details
    birth_date: '',
    gender: '',
    medical_conditions: '',
    no_medical_conditions: false,
    class_ids: [],
    discount_percent: 0,
    discount_note: '',
    discount_one_time: false,
    mailing_lists: [],
    send_invite: true,
    // Pro-rated mid-term joining
    is_mid_term: false,
    weeks_remaining: 6,
    term_length: 6  // 6 or 12 weeks
  });
  const [showGuardian2, setShowGuardian2] = useState(false);
  const [mailingLists, setMailingLists] = useState([]);
  const SIBLING_DISCOUNT_PERCENT = 30;
  const BUNDLE_DISCOUNT_PRICE = 45; // Discounted price for Street Dance/Ballet/Tap when bundled with UPA 3 Hour

  // Local price fallback — the backend returns raw fields (term_price,
  // six_week_price, subscription_price), and not every class has term_price set
  // (e.g. legacy subscription classes like Primatotterinas only have
  // subscription_price; new classes only have six_week_price).
  const priceForClass = (cls) => {
    if (!cls) return 0;
    return Number(
      cls.term_price ?? cls.six_week_price ?? cls.subscription_price ?? cls.monthly_price ?? 0
    ) || 0;
  };

  // Calculate total with bundle discount applied
  const calculateStudentTotal = (student, classes, siblingIndex = 0) => {
    const selectedClasses = classes.filter(c => student.class_ids.includes(c.id));
    
    // Check for bundle conditions
    const hasUPA3Hour = selectedClasses.some(c => c.name?.toLowerCase().includes('upa 3 hour'));
    const streetDanceClasses = selectedClasses.filter(c => c.name?.toLowerCase().includes('street dance'));
    const balletTapClasses = selectedClasses.filter(c => c.name?.toLowerCase().includes('ballet/tap') || c.name?.toLowerCase().includes('ballet tap'));
    
    // Bundle discount applies when UPA 3 Hour + at least one Street Dance + at least one Ballet/Tap
    const bundleApplies = hasUPA3Hour && streetDanceClasses.length > 0 && balletTapClasses.length > 0;
    
    let total = 0;
    let bundleSavings = 0;
    
    selectedClasses.forEach(cls => {
      let price = priceForClass(cls);
      
      // Apply bundle discount to Street Dance and Ballet/Tap classes
      if (bundleApplies) {
        const isStreetDance = cls.name?.toLowerCase().includes('street dance');
        const isBalletTap = cls.name?.toLowerCase().includes('ballet/tap') || cls.name?.toLowerCase().includes('ballet tap');
        
        if ((isStreetDance || isBalletTap) && price > BUNDLE_DISCOUNT_PRICE) {
          bundleSavings += price - BUNDLE_DISCOUNT_PRICE;
          price = BUNDLE_DISCOUNT_PRICE;
        }
      }
      
      total += price;
    });
    
    // Apply sibling discount for 2nd+ students
    const siblingDiscount = siblingIndex > 0 ? total * (SIBLING_DISCOUNT_PERCENT / 100) : 0;
    
    return {
      subtotal: total,
      bundleSavings,
      siblingDiscount,
      finalTotal: total - siblingDiscount,
      bundleApplies
    };
  };

  useEffect(() => {
    fetchClasses();
    fetchMailingLists();
    fetchPendingInvites();
  }, []);

  // Prefill the Add Student form if navigated here from the Trials tab
  useEffect(() => {
    const prefill = location.state?.prefillFromTrial;
    if (!prefill) return;
    setNewStudent(prev => ({
      ...prev,
      student_name: prefill.student_name || '',
      birth_date: prefill.birth_date || '',
      gender: prefill.gender || '',
      medical_conditions: prefill.medical_conditions || '',
      no_medical_conditions: !prefill.medical_conditions,
      parent_email: prefill.parent_email || '',
      parent_first_name: prefill.parent_first_name || '',
      parent_last_name: prefill.parent_last_name || '',
      parent_phone: prefill.parent_phone || '',
      guardian2_first_name: prefill.guardian2_first_name || '',
      guardian2_last_name: prefill.guardian2_last_name || '',
      guardian2_email: prefill.guardian2_email || '',
      guardian2_phone: prefill.guardian2_phone || '',
      class_ids: prefill.class_id ? [prefill.class_id] : [],
    }));
    if (prefill.guardian2_first_name) setShowGuardian2(true);
    setAddingSiblings(false);
    setAddModalOpen(true);
    toast.success(`Form pre-filled from trial — select classes for ${prefill.student_name || 'student'}`);
    // Clear the navigation state so refreshing doesn't re-prefill
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchClasses = async () => {
    try {
      const res = await classesAPI.getAll();
      setClasses(res.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
    }
  };

  const fetchMailingLists = async () => {
    try {
      const res = await api.get('/mailing-lists');
      setMailingLists(res.data?.lists || []);
    } catch (error) {
      console.error('Failed to fetch mailing lists:', error);
    }
  };

  const fetchPendingInvites = async () => {
    setLoadingPending(true);
    try {
      const res = await api.get('/students/pending-invites');
      setPendingInvites(res.data?.students || []);
    } catch (error) {
      console.error('Failed to fetch pending invites:', error);
    } finally {
      setLoadingPending(false);
    }
  };

  const handleBulkSendInvites = async () => {
    const idsToSend = selectedPending.length > 0 ? selectedPending : pendingInvites.map(s => s.id);
    
    if (idsToSend.length === 0) {
      toast.error('No students to send invites to');
      return;
    }

    setSendingBulkInvites(true);
    try {
      const response = await api.post('/students/send-invites', { student_ids: idsToSend });
      toast.success(`Sent ${response.data.sent_count} invitation emails!`);
      if (response.data.failed_count > 0) {
        toast.warning(`${response.data.failed_count} emails failed to send`);
      }
      // Refresh the pending list
      fetchPendingInvites();
      setSelectedPending([]);
    } catch (error) {
      console.error('Failed to send invites:', error);
      toast.error('Failed to send invitation emails');
    } finally {
      setSendingBulkInvites(false);
    }
  };

  const toggleSelectPending = (studentId) => {
    setSelectedPending(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAllPending = () => {
    if (selectedPending.length === pendingInvites.length) {
      setSelectedPending([]);
    } else {
      setSelectedPending(pendingInvites.map(s => s.id));
    }
  };

  const handleAddStudent = async (opts = {}) => {
    if (!newStudent.student_name || !newStudent.parent_email) {
      toast.error('Student name and parent email are required');
      return;
    }
    
    // Validate medical conditions
    if (!newStudent.medical_conditions.trim() && !newStudent.no_medical_conditions) {
      toast.error('Please either fill in medical conditions or tick "No known medical conditions"');
      return;
    }
    
    setAddingStudent(true);
    try {
      // If checkbox is ticked, set medical conditions to "None"
      const submitData = {
        ...newStudent,
        medical_conditions: newStudent.no_medical_conditions ? 'None' : newStudent.medical_conditions,
        // When retrying as a sibling, bypass the pending-email duplicate check
        is_sibling: opts.asSibling === true
      };

      // Registered-parent path: skip the invite flow and create the child
      // directly under the existing parent's account.
      if (opts.addToExistingParent === true) {
        const res = await api.post('/students/add-to-existing-parent', submitData);
        const childName = newStudent.student_name || 'Sibling';
        toast.success(`${childName} added under existing parent${res.data?.bookings_created ? ` (${res.data.bookings_created} class booking(s) pending payment)` : ''}`);
        setExistingParentPrompt(null);
        setAddModalOpen(false);
        setNewStudent({
          student_name: '',
          parent_email: '',
          parent_first_name: '',
          parent_last_name: '',
          parent_phone: '',
          guardian2_email: '',
          guardian2_first_name: '',
          guardian2_last_name: '',
          guardian2_phone: '',
          guardian2_receives_emails: true,
          birth_date: '',
          gender: '',
          medical_conditions: '',
          no_medical_conditions: false,
          class_ids: [],
          discount_percent: 0,
          discount_note: '',
          discount_one_time: false,
          mailing_lists: [],
          send_invite: true,
          is_mid_term: false,
          weeks_remaining: 6,
          term_length: 6
        });
        setShowGuardian2(false);
        return;
      }

      const res = await studentsAPI.add(submitData);
      toast.success(
        opts.asSibling
          ? `Sibling added! ${res.data.email_sent ? 'Invite email sent.' : 'Copy registration link to share.'}`
          : `Student added! ${res.data.email_sent ? 'Invite email sent.' : 'Copy registration link to share.'}`
      );
      
      if (!res.data.email_sent) {
        // Show the registration link
        navigator.clipboard.writeText(res.data.register_url);
        toast.info('Registration link copied to clipboard!');
      }
      
      setAddModalOpen(false);
      setSiblingPrompt(null);
      setNewStudent({
        student_name: '',
        parent_email: '',
        parent_first_name: '',
        parent_last_name: '',
        parent_phone: '',
        guardian2_email: '',
        guardian2_first_name: '',
        guardian2_last_name: '',
        guardian2_phone: '',
        guardian2_receives_emails: true,
        birth_date: '',
        gender: '',
        medical_conditions: '',
        no_medical_conditions: false,
        class_ids: [],
        discount_percent: 0,
        discount_note: '',
        discount_one_time: false,
        mailing_lists: [],
        send_invite: true,
        is_mid_term: false,
        weeks_remaining: 6,
        term_length: 6
      });
      setShowGuardian2(false);
      // Refresh pending invites list if invite wasn't sent
      if (!newStudent.send_invite) {
        fetchPendingInvites();
      }
    } catch (error) {
      const status = error.response?.status;
      const rawDetail = error.response?.data?.detail;
      // Always coerce to string so older code paths that do `.match()` /
      // `.test()` on it can't crash. If a future backend returns a structured
      // object, we still extract a message field.
      const detail = (typeof rawDetail === 'string')
        ? rawDetail
        : (rawDetail?.message || JSON.stringify(rawDetail || ''));

      // Parent already has a registered account → offer to add child directly under them.
      // Backend marks this case with the "PARENT_ALREADY_REGISTERED:" prefix (status 409).
      if (status === 409 && /^PARENT_ALREADY_REGISTERED:/i.test(detail)) {
        const parentName = detail.replace(/^PARENT_ALREADY_REGISTERED:\s*/i, '').split(' already')[0].trim();
        setExistingParentPrompt({
          parentName: parentName || newStudent.parent_email,
        });
        return;
      }

      // If the backend is blocking on an existing pending registration for
      // this email, offer to re-submit as a sibling (skips the duplicate check).
      // Match permissively so small wording changes don't break the prompt:
      //   - "A pending registration exists for this email (Student: X)..."
      //   - "Pending registration already exists..."
      //   - any 400 mentioning "(Student: X)" in the detail.
      const looksLikePendingDup =
        error.response?.status === 400 &&
        (
          /pending registration/i.test(detail) ||
          /registration .* exists/i.test(detail) ||
          /\(Student:/i.test(detail)
        );
      if (!opts.asSibling && looksLikePendingDup) {
        const m = detail.match(/Student:\s*([^)]+?)\)/i);
        setSiblingPrompt({
          existingStudentName: m ? m[1].trim() : 'an existing student',
          message: detail,
        });
      } else {
        toast.error(detail || 'Failed to add student');
      }
    } finally {
      setAddingStudent(false);
    }
  };

  const toggleClassSelection = (classId) => {
    setNewStudent(prev => ({
      ...prev,
      class_ids: prev.class_ids.includes(classId)
        ? prev.class_ids.filter(id => id !== classId)
        : [...prev.class_ids, classId]
    }));
  };

  // Multi-student functions
  const addSibling = () => {
    setStudents(prev => [...prev, { 
      ...emptyStudent, 
      // Auto-apply sibling discount for 2nd+ students
      discount_percent: SIBLING_DISCOUNT_PERCENT,
      discount_note: 'Sibling discount (30%)'
    }]);
  };

  const removeSibling = (index) => {
    if (students.length > 1) {
      setStudents(prev => {
        const updated = prev.filter((_, i) => i !== index);
        // Recalculate discounts - first student no discount, rest get sibling discount
        return updated.map((student, i) => ({
          ...student,
          discount_percent: i === 0 ? 0 : SIBLING_DISCOUNT_PERCENT,
          discount_note: i === 0 ? '' : 'Sibling discount (30%)'
        }));
      });
    }
  };

  const updateStudent = (index, field, value) => {
    setStudents(prev => prev.map((student, i) => 
      i === index ? { ...student, [field]: value } : student
    ));
  };

  const toggleStudentClass = (studentIndex, classId) => {
    setStudents(prev => prev.map((student, i) => {
      if (i !== studentIndex) return student;
      const newClassIds = student.class_ids.includes(classId)
        ? student.class_ids.filter(id => id !== classId)
        : [...student.class_ids, classId];
      return { ...student, class_ids: newClassIds };
    }));
  };

  const toggleStudentMailingList = (studentIndex, listName) => {
    setStudents(prev => prev.map((student, i) => {
      if (i !== studentIndex) return student;
      const newLists = student.mailing_lists.includes(listName)
        ? student.mailing_lists.filter(n => n !== listName)
        : [...student.mailing_lists, listName];
      return { ...student, mailing_lists: newLists };
    }));
  };

  const handleAddMultipleStudents = async () => {
    // Validate parent info
    if (!parentInfo.parent_email || !parentInfo.parent_first_name || !parentInfo.parent_last_name) {
      toast.error('Please fill in parent details');
      return;
    }
    
    // Validate each student
    for (let i = 0; i < students.length; i++) {
      if (!students[i].student_name) {
        toast.error(`Please enter name for student ${i + 1}`);
        return;
      }
      if (students[i].class_ids.length === 0) {
        toast.error(`Please select at least one class for ${students[i].student_name}`);
        return;
      }
    }
    
    setAddingStudent(true);
    try {
      // Use the new batch endpoint that sends ONE combined email
      const payload = {
        parent_email: parentInfo.parent_email,
        parent_first_name: parentInfo.parent_first_name,
        parent_last_name: parentInfo.parent_last_name,
        parent_phone: parentInfo.parent_phone,
        guardian2_email: parentInfo.guardian2_email,
        guardian2_first_name: parentInfo.guardian2_first_name,
        guardian2_last_name: parentInfo.guardian2_last_name,
        guardian2_phone: parentInfo.guardian2_phone,
        guardian2_receives_emails: parentInfo.guardian2_receives_emails,
        students: students.map((student, i) => ({
          student_name: student.student_name,
          birth_date: student.birth_date,
          gender: student.gender,
          medical_conditions: student.no_medical_conditions ? 'None' : student.medical_conditions,
          class_ids: student.class_ids,
          discount_percent: student.discount_percent,
          discount_note: student.discount_note,
          mailing_lists: student.mailing_lists
        })),
        send_invite: parentInfo.send_invite
      };
      
      const res = await studentsAPI.addSiblings(payload);
      
      toast.success(`Successfully added ${students.length} student${students.length > 1 ? 's' : ''}! ${parentInfo.send_invite ? 'Combined invite email sent.' : ''}`);
      setAddModalOpen(false);
      
      // Reset form
      setStudents([{ ...emptyStudent }]);
      setParentInfo({
        parent_email: '',
        parent_first_name: '',
        parent_last_name: '',
        parent_phone: '',
        guardian2_email: '',
        guardian2_first_name: '',
        guardian2_last_name: '',
        guardian2_phone: '',
        guardian2_receives_emails: true,
        send_invite: true
      });
      setShowGuardian2(false);
      
      if (!parentInfo.send_invite) {
        fetchPendingInvites();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add students');
    } finally {
      setAddingStudent(false);
    }
  };

  const toggleMailingList = (listName) => {
    setNewStudent(prev => ({
      ...prev,
      mailing_lists: prev.mailing_lists.includes(listName)
        ? prev.mailing_lists.filter(n => n !== listName)
        : [...prev.mailing_lists, listName]
    }));
  };

  const expectedColumns = [
    'student_name', 'birth_date', 'gender', 'address',
    'guardian1_name', 'guardian1_email', 'guardian1_phone',
    'guardian2_name', 'guardian2_email', 'guardian2_phone',
    'medical_conditions'
  ];

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    parseCSV(selectedFile);
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file must have a header row and at least one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === headers.length) {
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx]?.trim() || '';
          });
          // Validate required fields
          row.isValid = !!(row.student_name && row.guardian1_email);
          row.errors = [];
          if (!row.student_name) row.errors.push('Missing student name');
          if (!row.guardian1_email) row.errors.push('Missing guardian email');
          if (row.guardian1_email && !isValidEmail(row.guardian1_email)) {
            row.errors.push('Invalid guardian 1 email');
            row.isValid = false;
          }
          data.push(row);
        }
      }

      setParsedData(data);
      setPreviewOpen(true);
    };
    reader.readAsText(file);
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleImport = async () => {
    const validStudents = parsedData.filter(s => s.isValid);
    if (validStudents.length === 0) {
      toast.error('No valid students to import');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/students/import', { students: validStudents });
      setImportResults(response.data);
      setPreviewOpen(false);
      toast.success(`Successfully imported ${response.data.imported} students`);
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import students');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvites = async () => {
    if (!importResults?.imported_ids?.length) {
      toast.error('No students to send invites to');
      return;
    }

    setSendingEmails(true);
    try {
      const response = await api.post('/students/send-invites', { 
        student_ids: importResults.imported_ids 
      });
      toast.success(`Sent ${response.data.sent} invitation emails`);
      setImportResults({ ...importResults, invites_sent: true });
    } catch (error) {
      console.error('Failed to send invites:', error);
      toast.error('Failed to send invitation emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const downloadTemplate = () => {
    const headers = expectedColumns.join(',');
    const sampleRow = 'John Smith,2015-05-15,Male,"123 Main St, Milton Keynes MK1 1AA",Jane Smith,jane@example.com,07700900123,Bob Smith,bob@example.com,07700900456,Mild asthma';
    const csv = `${headers}\n${sampleRow}`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const validCount = parsedData.filter(s => s.isValid).length;
  const invalidCount = parsedData.filter(s => !s.isValid).length;

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="student-import-page">
      <Navbar />
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#1a1a2e] to-[#0a0a0a] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF007F] to-purple-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                Student Import
              </h1>
              <p className="text-white/60 mt-2">Import students from CSV or add individually</p>
            </div>
            <Button
              onClick={() => setAddModalOpen(true)}
              className="bg-gradient-to-r from-[#7000FF] to-[#FF007F] hover:opacity-90"
              data-testid="add-student-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Student
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Instructions */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#FF007F]" />
              CSV Format Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-white/70 mb-4">Your CSV file should contain the following columns:</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {expectedColumns.map(col => (
                <Badge key={col} className="bg-white/10 text-white/80 font-mono text-xs">
                  {col}
                </Badge>
              ))}
            </div>
            <Button 
              variant="outline" 
              onClick={downloadTemplate}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-8">
            <div 
              className="border-2 border-dashed border-white/20 rounded-xl p-12 text-center hover:border-[#FF007F]/50 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv"
                className="hidden"
                data-testid="csv-file-input"
              />
              <Upload className="w-12 h-12 text-white/30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {file ? file.name : 'Drop your CSV file here'}
              </h3>
              <p className="text-white/50">or click to browse</p>
            </div>
          </CardContent>
        </Card>

        {/* Import Results */}
        {importResults && (
          <Card className="bg-green-500/10 border-green-500/30 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">Import Complete!</h3>
                    <p className="text-white/70">{importResults.imported} students imported successfully</p>
                  </div>
                </div>
                {!importResults.invites_sent && (
                  <Button 
                    onClick={handleSendInvites}
                    disabled={sendingEmails}
                    className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
                    data-testid="send-invites-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {sendingEmails ? 'Sending...' : 'Send Parent Invites'}
                  </Button>
                )}
                {importResults.invites_sent && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Invites Sent
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Workflow Info */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-white">How it works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#FF007F]/20 text-[#FF007F] flex items-center justify-center font-bold">1</div>
                <div>
                  <h4 className="text-white font-semibold">Upload CSV</h4>
                  <p className="text-white/60 text-sm">Import your student data with guardian contact details</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#FF007F]/20 text-[#FF007F] flex items-center justify-center font-bold">2</div>
                <div>
                  <h4 className="text-white font-semibold">Send Invitations</h4>
                  <p className="text-white/60 text-sm">Parents receive an email with a registration link</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#FF007F]/20 text-[#FF007F] flex items-center justify-center font-bold">3</div>
                <div>
                  <h4 className="text-white font-semibold">Parent Registration</h4>
                  <p className="text-white/60 text-sm">Parents create their account, confirm child details, and select classes</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Invitations Section */}
        <Card className="bg-white/5 border-white/10 mb-6" data-testid="pending-invites-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#FF007F]" />
                Pending Invitations
                {pendingInvites.length > 0 && (
                  <Badge className="bg-[#FF007F]/20 text-[#FF007F] ml-2">
                    {pendingInvites.length}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchPendingInvites}
                  disabled={loadingPending}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  {loadingPending ? 'Loading...' : 'Refresh'}
                </Button>
                {pendingInvites.length > 0 && (
                  <Button
                    onClick={handleBulkSendInvites}
                    disabled={sendingBulkInvites}
                    className="bg-gradient-to-r from-[#7000FF] to-[#FF007F] hover:opacity-90"
                    data-testid="bulk-send-invites-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingBulkInvites ? 'Sending...' : selectedPending.length > 0 ? `Send ${selectedPending.length} Invites` : `Send All ${pendingInvites.length} Invites`}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loadingPending ? (
              <div className="text-center py-8 text-white/60">Loading pending invitations...</div>
            ) : pendingInvites.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-white/70">No pending invitations</p>
                <p className="text-white/50 text-sm">All students have been invited or are using different registration methods</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                  <Checkbox
                    id="select-all-pending"
                    checked={selectedPending.length === pendingInvites.length && pendingInvites.length > 0}
                    onCheckedChange={toggleSelectAllPending}
                  />
                  <Label htmlFor="select-all-pending" className="text-white/70 text-sm cursor-pointer">
                    Select all ({pendingInvites.length} students)
                  </Label>
                </div>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {pendingInvites.map(student => (
                    <div 
                      key={student.id} 
                      className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                        selectedPending.includes(student.id) 
                          ? 'bg-[#FF007F]/10 border-[#FF007F]/30' 
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <Checkbox
                        checked={selectedPending.includes(student.id)}
                        onCheckedChange={() => toggleSelectPending(student.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{student.student_name}</span>
                          <Badge className="bg-white/10 text-white/60 text-xs">
                            {student.gender || 'Unknown'}
                          </Badge>
                        </div>
                        <div className="text-white/50 text-sm mt-1">
                          Parent: {student.parent_email}
                        </div>
                        {student.class_names && student.class_names.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {student.class_names.map((name, idx) => (
                              <Badge key={idx} className="bg-purple-500/20 text-purple-300 text-xs">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-white/40 text-xs">
                        Added {new Date(student.created_at).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#FF007F]" />
              Preview Import Data
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Review the data before importing. Invalid rows will be skipped.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex gap-4 py-2">
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              {validCount} Valid
            </Badge>
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
              <XCircle className="w-3 h-3 mr-1" />
              {invalidCount} Invalid
            </Badge>
          </div>

          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 sticky top-0">
                <tr>
                  <th className="p-2 text-left text-white/70">Status</th>
                  <th className="p-2 text-left text-white/70">Student Name</th>
                  <th className="p-2 text-left text-white/70">DOB</th>
                  <th className="p-2 text-left text-white/70">Guardian 1</th>
                  <th className="p-2 text-left text-white/70">Email</th>
                  <th className="p-2 text-left text-white/70">Issues</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.map((row, idx) => (
                  <tr key={idx} className={`border-t border-white/5 ${!row.isValid ? 'bg-red-500/10' : ''}`}>
                    <td className="p-2">
                      {row.isValid ? (
                        <CheckCircle className="w-4 h-4 text-green-400" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-400" />
                      )}
                    </td>
                    <td className="p-2 text-white">{row.student_name || '-'}</td>
                    <td className="p-2 text-white/70">{row.birth_date || '-'}</td>
                    <td className="p-2 text-white/70">{row.guardian1_name || '-'}</td>
                    <td className="p-2 text-white/70">{row.guardian1_email || '-'}</td>
                    <td className="p-2">
                      {row.errors?.length > 0 && (
                        <span className="text-red-400 text-xs">{row.errors.join(', ')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <DialogFooter className="border-t border-white/10 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setPreviewOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              disabled={loading || validCount === 0}
              className="bg-[#FF007F] hover:bg-[#D6006A] text-white"
              data-testid="confirm-import-btn"
            >
              {loading ? 'Importing...' : `Import ${validCount} Students`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Student Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#7000FF]" />
              {addingSiblings ? 'Add Students (Siblings)' : 'Add New Student'}
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {addingSiblings 
                ? 'Add multiple students from the same family. Siblings automatically get 30% discount!'
                : 'Add a student and send a registration invite to their parent'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* Siblings Toggle */}
          <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
            <Checkbox
              id="add-siblings-toggle"
              checked={addingSiblings}
              onCheckedChange={(checked) => {
                setAddingSiblings(checked);
                if (checked) {
                  // Initialize first student from current newStudent data
                  setStudents([{
                    student_name: newStudent.student_name,
                    birth_date: newStudent.birth_date,
                    gender: newStudent.gender,
                    medical_conditions: newStudent.medical_conditions,
                    no_medical_conditions: newStudent.no_medical_conditions,
                    class_ids: newStudent.class_ids,
                    discount_percent: 0,
                    discount_note: '',
                    mailing_lists: newStudent.mailing_lists
                  }]);
                  setParentInfo({
                    parent_email: newStudent.parent_email,
                    parent_first_name: newStudent.parent_first_name,
                    parent_last_name: newStudent.parent_last_name,
                    parent_phone: newStudent.parent_phone,
                    guardian2_email: newStudent.guardian2_email,
                    guardian2_first_name: newStudent.guardian2_first_name,
                    guardian2_last_name: newStudent.guardian2_last_name,
                    guardian2_phone: newStudent.guardian2_phone,
                    guardian2_receives_emails: newStudent.guardian2_receives_emails,
                    send_invite: newStudent.send_invite
                  });
                }
              }}
              className="border-emerald-500 data-[state=checked]:bg-emerald-500"
            />
            <div>
              <Label htmlFor="add-siblings-toggle" className="text-emerald-400 font-medium cursor-pointer">
                Adding siblings from same family?
              </Label>
              <p className="text-xs text-white/50">Check this to add multiple students with shared parent details. 2nd+ students get 30% off!</p>
            </div>
          </div>
          
          {addingSiblings ? (
            /* MULTI-STUDENT (SIBLINGS) MODE */
            <div className="space-y-6 py-2">
              {/* Shared Parent Info */}
              <div className="space-y-4 p-4 bg-[#7000FF]/10 rounded-lg border border-[#7000FF]/30">
                <h4 className="text-sm font-semibold text-[#7000FF] uppercase tracking-wide">Parent/Guardian (Shared)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">First Name *</Label>
                    <Input
                      value={parentInfo.parent_first_name}
                      onChange={(e) => setParentInfo({...parentInfo, parent_first_name: e.target.value})}
                      placeholder="First name"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Last Name *</Label>
                    <Input
                      value={parentInfo.parent_last_name}
                      onChange={(e) => setParentInfo({...parentInfo, parent_last_name: e.target.value})}
                      placeholder="Last name"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Email *</Label>
                    <Input
                      type="email"
                      value={parentInfo.parent_email}
                      onChange={(e) => setParentInfo({...parentInfo, parent_email: e.target.value})}
                      placeholder="parent@email.com"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Phone</Label>
                    <Input
                      value={parentInfo.parent_phone}
                      onChange={(e) => setParentInfo({...parentInfo, parent_phone: e.target.value})}
                      placeholder="Phone number"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Students List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide">
                    Students ({students.length})
                  </h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSibling}
                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Add Another Sibling
                  </Button>
                </div>
                
                {students.map((student, index) => (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border ${index === 0 ? 'bg-white/5 border-white/20' : 'bg-emerald-500/10 border-emerald-500/30'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge className={index === 0 ? 'bg-[#7000FF]' : 'bg-emerald-500'}>
                          {index === 0 ? 'Student 1 (Full Price)' : `Student ${index + 1} (30% Off)`}
                        </Badge>
                      </div>
                      {students.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSibling(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label className="text-white/70 text-sm">Student Name *</Label>
                        <Input
                          value={student.student_name}
                          onChange={(e) => updateStudent(index, 'student_name', e.target.value)}
                          placeholder="Full name"
                          className="bg-white/5 border-white/10 text-white mt-1"
                        />
                      </div>
                      <DateOfBirthInput
                        value={student.birth_date}
                        onChange={(v) => updateStudent(index, 'birth_date', v)}
                        label="Date of Birth"
                      />
                      <div>
                        <Label className="text-white/70 text-sm">Gender</Label>
                        <Select
                          value={student.gender}
                          onValueChange={(v) => updateStudent(index, 'gender', v)}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a2e] border-white/10">
                            <SelectItem value="Male" className="text-white">Male</SelectItem>
                            <SelectItem value="Female" className="text-white">Female</SelectItem>
                            <SelectItem value="Other" className="text-white">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-white/70 text-sm">Medical Conditions</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            value={student.medical_conditions}
                            onChange={(e) => updateStudent(index, 'medical_conditions', e.target.value)}
                            placeholder="Any conditions, allergies..."
                            className="bg-white/5 border-white/10 text-white flex-1"
                            disabled={student.no_medical_conditions}
                          />
                          <div className="flex items-center gap-1 px-2">
                            <Checkbox
                              checked={student.no_medical_conditions}
                              onCheckedChange={(checked) => {
                                updateStudent(index, 'no_medical_conditions', checked);
                                if (checked) updateStudent(index, 'medical_conditions', '');
                              }}
                            />
                            <span className="text-white/50 text-xs">None</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Classes Selection */}
                      <div className="col-span-2">
                        <Label className="text-white/70 text-sm">Select Classes *</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
                          {classes.map(cls => (
                            <div
                              key={cls.id}
                              onClick={() => toggleStudentClass(index, cls.id)}
                              className={`p-2 rounded cursor-pointer transition-all text-sm ${
                                student.class_ids.includes(cls.id)
                                  ? 'bg-[#FF007F]/30 border border-[#FF007F]'
                                  : 'bg-white/5 border border-white/10 hover:bg-white/10'
                              }`}
                            >
                              <span className="text-white text-xs">{cls.name}</span>
                              {(() => {
                                // Check if bundle discount applies for this student
                                const selectedClasses = classes.filter(c => student.class_ids.includes(c.id));
                                const hasUPA3Hour = selectedClasses.some(c => c.name?.toLowerCase().includes('upa 3 hour'));
                                const hasStreetDance = selectedClasses.some(c => c.name?.toLowerCase().includes('street dance'));
                                const hasBalletTap = selectedClasses.some(c => c.name?.toLowerCase().includes('ballet/tap') || c.name?.toLowerCase().includes('ballet tap'));
                                const bundleApplies = hasUPA3Hour && hasStreetDance && hasBalletTap;
                                
                                const isStreetDance = cls.name?.toLowerCase().includes('street dance');
                                const isBalletTap = cls.name?.toLowerCase().includes('ballet/tap') || cls.name?.toLowerCase().includes('ballet tap');
                                const basePrice = priceForClass(cls);
                                const isBundleDiscounted = bundleApplies && (isStreetDance || isBalletTap) && basePrice > BUNDLE_DISCOUNT_PRICE;
                                
                                let price = basePrice;
                                if (isBundleDiscounted) {
                                  price = BUNDLE_DISCOUNT_PRICE;
                                }
                                
                                // Apply sibling discount for 2nd+ students
                                const finalPrice = index > 0 ? price * 0.7 : price;
                                
                                return (
                                  <span className="text-white/50 text-xs block">
                                    {isBundleDiscounted && (
                                      <span className="line-through mr-1 text-white/30">£{basePrice.toFixed(2)}</span>
                                    )}
                                    £{finalPrice.toFixed(2)}
                                    {isBundleDiscounted && <span className="text-cyan-400 ml-1">(bundle)</span>}
                                    {index > 0 && <span className="text-emerald-400 ml-1">(30% off)</span>}
                                  </span>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Total Summary */}
                {students.some(s => s.class_ids.length > 0) && (() => {
                  // Calculate totals for all students
                  let grandTotal = 0;
                  let totalBundleSavings = 0;
                  let totalSiblingDiscount = 0;
                  
                  students.forEach((student, idx) => {
                    const result = calculateStudentTotal(student, classes, idx);
                    grandTotal += result.finalTotal;
                    totalBundleSavings += result.bundleSavings;
                    totalSiblingDiscount += result.siblingDiscount;
                  });
                  
                  const hasBundleDiscount = totalBundleSavings > 0;
                  const hasSiblingDiscount = totalSiblingDiscount > 0;
                  
                  return (
                    <div className="p-4 bg-gradient-to-r from-[#7000FF]/20 to-[#FF007F]/20 rounded-lg border border-[#FF007F]/30">
                      <div className="flex justify-between items-center text-lg">
                        <span className="text-white font-semibold">Total for {students.length} student(s)</span>
                        <span className="text-[#FF007F] font-bold">
                          £{grandTotal.toFixed(2)}
                        </span>
                      </div>
                      {hasBundleDiscount && (
                        <p className="text-cyan-400 text-sm mt-1">
                          🎁 Bundle discount applied: Saving £{totalBundleSavings.toFixed(2)} on Street Dance + Ballet/Tap combo!
                        </p>
                      )}
                      {hasSiblingDiscount && (
                        <p className="text-emerald-400 text-sm mt-1">
                          💰 Sibling discount: Saving £{totalSiblingDiscount.toFixed(2)} (30% off for siblings)
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Send Invite Toggle */}
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <Checkbox
                  id="send-invite-siblings"
                  checked={parentInfo.send_invite}
                  onCheckedChange={(checked) => setParentInfo({...parentInfo, send_invite: checked})}
                />
                <div>
                  <Label htmlFor="send-invite-siblings" className="text-white cursor-pointer">Send registration invite</Label>
                  <p className="text-xs text-white/50">Parent receives email to complete registration</p>
                </div>
              </div>
            </div>
          ) : (
          /* SINGLE STUDENT MODE (Original) */
          <div className="space-y-6 py-4">
            {/* Student Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide">Student Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-white/70">Student Full Name *</Label>
                  <Input
                    value={newStudent.student_name}
                    onChange={(e) => setNewStudent({...newStudent, student_name: e.target.value})}
                    placeholder="e.g. Emma Johnson"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <DateOfBirthInput
                  value={newStudent.birth_date}
                  onChange={(v) => setNewStudent({...newStudent, birth_date: v})}
                  label="Date of Birth"
                />
                <div>
                  <Label className="text-white/70">Gender</Label>
                  <Select
                    value={newStudent.gender}
                    onValueChange={(v) => setNewStudent({...newStudent, gender: v})}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a2e] border-white/10">
                      <SelectItem value="Male" className="text-white">Male</SelectItem>
                      <SelectItem value="Female" className="text-white">Female</SelectItem>
                      <SelectItem value="Other" className="text-white">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-white/70">Medical Conditions *</Label>
                  <Textarea
                    value={newStudent.medical_conditions}
                    onChange={(e) => {
                      setNewStudent({...newStudent, medical_conditions: e.target.value});
                      if (e.target.value.trim()) {
                        setNewStudent(prev => ({...prev, medical_conditions: e.target.value, no_medical_conditions: false}));
                      }
                    }}
                    placeholder="Any allergies, conditions, or special needs..."
                    className={`bg-white/5 border-white/10 text-white mt-1 ${newStudent.no_medical_conditions ? 'opacity-50' : ''}`}
                    rows={2}
                    disabled={newStudent.no_medical_conditions}
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox
                      id="no-medical-student"
                      checked={newStudent.no_medical_conditions}
                      onCheckedChange={(checked) => {
                        setNewStudent({...newStudent, no_medical_conditions: checked, medical_conditions: checked ? '' : newStudent.medical_conditions});
                      }}
                      className="border-white/30 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    <Label htmlFor="no-medical-student" className="text-white/60 text-sm cursor-pointer">
                      No known medical conditions
                    </Label>
                  </div>
                  {!newStudent.medical_conditions.trim() && !newStudent.no_medical_conditions && (
                    <p className="text-amber-400 text-xs mt-1">
                      Please fill in or tick the checkbox
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Parent Info */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide">Parent/Guardian 1 (Primary)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">First Name</Label>
                  <Input
                    value={newStudent.parent_first_name}
                    onChange={(e) => setNewStudent({...newStudent, parent_first_name: e.target.value})}
                    placeholder="First name"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Last Name</Label>
                  <Input
                    value={newStudent.parent_last_name}
                    onChange={(e) => setNewStudent({...newStudent, parent_last_name: e.target.value})}
                    placeholder="Last name"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Email *</Label>
                  <Input
                    type="email"
                    value={newStudent.parent_email}
                    onChange={(e) => setNewStudent({...newStudent, parent_email: e.target.value})}
                    placeholder="parent@example.com"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Phone</Label>
                  <Input
                    value={newStudent.parent_phone}
                    onChange={(e) => setNewStudent({...newStudent, parent_phone: e.target.value})}
                    placeholder="07700 900123"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Second Guardian (Optional) */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide">Parent/Guardian 2 (Optional)</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowGuardian2(!showGuardian2)}
                  className="text-[#FF007F] hover:text-[#FF007F]/80"
                >
                  {showGuardian2 ? 'Hide' : 'Add Second Parent'}
                </Button>
              </div>
              {showGuardian2 && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div>
                    <Label className="text-white/70">First Name</Label>
                    <Input
                      value={newStudent.guardian2_first_name}
                      onChange={(e) => setNewStudent({...newStudent, guardian2_first_name: e.target.value})}
                      placeholder="First name"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Last Name</Label>
                    <Input
                      value={newStudent.guardian2_last_name}
                      onChange={(e) => setNewStudent({...newStudent, guardian2_last_name: e.target.value})}
                      placeholder="Last name"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Email</Label>
                    <Input
                      type="email"
                      value={newStudent.guardian2_email}
                      onChange={(e) => setNewStudent({...newStudent, guardian2_email: e.target.value})}
                      placeholder="guardian2@example.com"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Phone</Label>
                    <Input
                      value={newStudent.guardian2_phone}
                      onChange={(e) => setNewStudent({...newStudent, guardian2_phone: e.target.value})}
                      placeholder="07700 900456"
                      className="bg-white/5 border-white/10 text-white mt-1"
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-3 pt-2 border-t border-white/10 mt-2">
                    <Checkbox
                      id="guardian2-receives-emails"
                      checked={newStudent.guardian2_receives_emails}
                      onCheckedChange={(checked) => setNewStudent({...newStudent, guardian2_receives_emails: checked})}
                      className="border-white/30"
                    />
                    <Label htmlFor="guardian2-receives-emails" className="text-white/70 text-sm cursor-pointer">
                      Second guardian receives all school emails
                    </Label>
                  </div>
                </div>
              )}
            </div>

            {/* Class Selection */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Pre-select Classes
              </h4>
              <p className="text-xs text-white/50">These classes will be pre-selected when the parent registers</p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white/5 rounded-lg">
                {classes.map(cls => (
                  <div 
                    key={cls.id}
                    onClick={() => toggleClassSelection(cls.id)}
                    className={`flex items-center justify-between gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                      newStudent.class_ids.includes(cls.id)
                        ? 'bg-[#7000FF]/20 border border-[#7000FF]/50'
                        : 'bg-white/5 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={newStudent.class_ids.includes(cls.id)}
                        className="border-white/30"
                      />
                      <span className="text-sm text-white">{cls.name}</span>
                    </div>
                    <span className="text-xs text-[#FF007F] font-semibold">£{priceForClass(cls).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {newStudent.class_ids.length > 0 && (() => {
                const selectedClasses = classes.filter(cls => newStudent.class_ids.includes(cls.id));
                const hasUPA3Hour = selectedClasses.some(c => c.name?.toLowerCase().includes('upa 3 hour'));
                const hasStreetDance = selectedClasses.some(c => c.name?.toLowerCase().includes('street dance'));
                const hasBalletTap = selectedClasses.some(c => c.name?.toLowerCase().includes('ballet/tap') || c.name?.toLowerCase().includes('ballet tap'));
                const bundleApplies = hasUPA3Hour && hasStreetDance && hasBalletTap;
                
                let subtotal = 0;
                let bundleSavings = 0;
                
                selectedClasses.forEach(cls => {
                  let price = priceForClass(cls);
                  const isStreetDance = cls.name?.toLowerCase().includes('street dance');
                  const isBalletTap = cls.name?.toLowerCase().includes('ballet/tap') || cls.name?.toLowerCase().includes('ballet tap');
                  
                  if (bundleApplies && (isStreetDance || isBalletTap) && price > BUNDLE_DISCOUNT_PRICE) {
                    bundleSavings += price - BUNDLE_DISCOUNT_PRICE;
                    price = BUNDLE_DISCOUNT_PRICE;
                  }
                  subtotal += price;
                });
                
                const discountAmount = newStudent.discount_percent > 0 ? subtotal * (newStudent.discount_percent / 100) : 0;
                const finalTotal = subtotal - discountAmount;
                
                return (
                  <div className="p-3 bg-[#FF007F]/10 rounded-lg border border-[#FF007F]/30">
                    <div className="flex justify-between items-center">
                      <span className="text-white">{newStudent.class_ids.length} class(es) selected</span>
                      <span className="text-[#FF007F] font-bold">
                        Total: £{finalTotal.toFixed(2)}
                      </span>
                    </div>
                    {bundleSavings > 0 && (
                      <div className="mt-1 text-cyan-400 text-sm">
                        🎁 Bundle discount: Saving £{bundleSavings.toFixed(2)}
                      </div>
                    )}
                    {newStudent.discount_percent > 0 && (
                      <div className="flex justify-between items-center mt-1 pt-1 border-t border-[#FF007F]/30">
                        <span className="text-green-400 text-sm">After {newStudent.discount_percent}% discount</span>
                        <span className="text-green-400 font-bold">
                          £{(subtotal * (1 - newStudent.discount_percent / 100)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Discount */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide flex items-center gap-2">
                <Percent className="w-4 h-4" />
                Discount (Optional)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Discount %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={newStudent.discount_percent}
                    onChange={(e) => setNewStudent({...newStudent, discount_percent: parseFloat(e.target.value) || 0})}
                    placeholder="0"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Discount Reason</Label>
                  <Input
                    value={newStudent.discount_note}
                    onChange={(e) => setNewStudent({...newStudent, discount_note: e.target.value})}
                    placeholder="e.g. Sibling discount, Staff child"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
              </div>
              {newStudent.discount_percent > 0 && (
                <>
                  <div className="flex items-center gap-3 mt-3">
                    <Checkbox
                      id="discount_one_time"
                      checked={!!newStudent.discount_one_time}
                      onCheckedChange={(checked) => setNewStudent({...newStudent, discount_one_time: !!checked})}
                      data-testid="discount-one-time-checkbox"
                    />
                    <Label htmlFor="discount_one_time" className="text-white/80 cursor-pointer text-sm">
                      <span className="font-medium">First term only</span>
                      <span className="text-white/50 block text-xs">Discount auto-clears after first successful payment (e.g. trial offer).</span>
                    </Label>
                  </div>
                  <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-green-400 text-sm">
                      {newStudent.discount_one_time
                        ? `🎉 This student will receive ${newStudent.discount_percent}% off their FIRST term only — renewals charge full price.`
                        : `🎉 This student will receive ${newStudent.discount_percent}% off the initial term. Renewals already charge full price regardless.`}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Mid-Term Joining (Pro-rated Pricing) */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Mid-Term Joining
              </h4>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_mid_term"
                  checked={newStudent.is_mid_term}
                  onCheckedChange={(checked) => setNewStudent({...newStudent, is_mid_term: checked})}
                />
                <Label htmlFor="is_mid_term" className="text-white cursor-pointer">
                  Student is joining mid-term (pro-rated pricing)
                </Label>
              </div>
              
              {newStudent.is_mid_term && (
                <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30 space-y-4">
                  <p className="text-amber-300 text-sm">
                    ⚡ Pro-rated pricing will be calculated based on remaining weeks. Parent will see both pro-rated and full-term options.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Weeks Remaining</Label>
                      <Input
                        type="number"
                        min="1"
                        max="12"
                        value={newStudent.weeks_remaining}
                        onChange={(e) => setNewStudent({...newStudent, weeks_remaining: parseInt(e.target.value) || 1})}
                        placeholder="e.g. 4"
                        className="bg-white/5 border-white/10 text-white mt-1"
                      />
                      <p className="text-xs text-white/50 mt-1">How many weeks left in the current term?</p>
                    </div>
                    <div>
                      <Label className="text-white/70">Term Length</Label>
                      <Select
                        value={String(newStudent.term_length)}
                        onValueChange={(value) => setNewStudent({...newStudent, term_length: parseInt(value)})}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="6">6-week term</SelectItem>
                          <SelectItem value="12">12-week term</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-white/50 mt-1">Total weeks in a full term</p>
                    </div>
                  </div>
                  {newStudent.weeks_remaining > 0 && newStudent.weeks_remaining <= newStudent.term_length && (
                    <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                      <p className="text-blue-300 text-sm">
                        📊 Parent will be offered: Pro-rated price ({newStudent.weeks_remaining}/{newStudent.term_length} weeks) or Full {newStudent.term_length}-week term price
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mailing Lists */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-white/70 uppercase tracking-wide flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Mailing Lists
              </h4>
              <p className="text-xs text-white/50">Assign this student to mailing lists for targeted communications</p>
              <div className="flex flex-wrap gap-2">
                {mailingLists.map(list => (
                  <div
                    key={list.name}
                    onClick={() => toggleMailingList(list.name)}
                    className={`px-3 py-1.5 rounded-full cursor-pointer transition-all text-sm ${
                      newStudent.mailing_lists.includes(list.name)
                        ? 'bg-purple-500/30 border border-purple-500 text-purple-200'
                        : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    {list.name}
                  </div>
                ))}
              </div>
              {newStudent.mailing_lists.length > 0 && (
                <p className="text-sm text-purple-400">{newStudent.mailing_lists.length} list(s) selected</p>
              )}
            </div>

            {/* Send Invite */}
            <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg">
              <Checkbox
                id="send-invite"
                checked={newStudent.send_invite}
                onCheckedChange={(checked) => setNewStudent({...newStudent, send_invite: checked})}
              />
              <div>
                <Label htmlFor="send-invite" className="text-white cursor-pointer">Send registration invite email</Label>
                <p className="text-xs text-white/50">Parent will receive an email with a link to complete registration</p>
              </div>
            </div>
          </div>
          )}

          <DialogFooter className="border-t border-white/10 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setAddModalOpen(false)}
              className="border-white/10 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={addingSiblings ? handleAddMultipleStudents : handleAddStudent}
              disabled={addingStudent || (addingSiblings 
                ? (!parentInfo.parent_email || !parentInfo.parent_first_name || students.some(s => !s.student_name || s.class_ids.length === 0))
                : (!newStudent.student_name || !newStudent.parent_email)
              )}
              className="bg-gradient-to-r from-[#7000FF] to-[#FF007F]"
              data-testid="add-student-submit-btn"
            >
              {addingStudent ? 'Adding...' : (addingSiblings ? `Add ${students.length} Student${students.length > 1 ? 's' : ''}` : 'Add Student & Send Invite')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sibling-of-pending prompt — shown when add-student is blocked because
          a pending registration already exists for this parent email. */}
      <Dialog open={!!siblingPrompt} onOpenChange={(o) => { if (!o) setSiblingPrompt(null); }}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md" data-testid="sibling-prompt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#FF007F]" />
              Looks like a sibling
            </DialogTitle>
            <DialogDescription className="text-white/70">
              {siblingPrompt && (
                <>
                  A pending registration already exists for <strong className="text-white">{newStudent.parent_email}</strong> ({siblingPrompt.existingStudentName}).
                  <br /><br />
                  If <strong className="text-white">{newStudent.student_name || 'this student'}</strong> is their sibling, click below to add them under the same parent. The 30% sibling discount will apply automatically on renewal flows.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setSiblingPrompt(null)}
              className="border-[#333] text-white"
              data-testid="sibling-prompt-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleAddStudent({ asSibling: true })}
              disabled={addingStudent}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="sibling-prompt-confirm"
            >
              {addingStudent ? 'Adding…' : 'Yes, add as sibling'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing-registered-parent prompt — shown when parent already has
          a user account; offers to add child directly under that account. */}
      <Dialog open={!!existingParentPrompt} onOpenChange={(o) => { if (!o) setExistingParentPrompt(null); }}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md" data-testid="existing-parent-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-400" />
              Parent already registered
            </DialogTitle>
            <DialogDescription className="text-white/70">
              {existingParentPrompt && (
                <>
                  <strong className="text-white">{existingParentPrompt.parentName}</strong> already has an account on UPA.
                  <br /><br />
                  If <strong className="text-white">{newStudent.student_name || 'this student'}</strong> is their child, click below to add them as a sibling under the existing account. The selected classes will be added as pending bookings — use Pay Now / Take Payment on the new student to charge them afterwards.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 mt-2">
            <Button
              variant="outline"
              onClick={() => setExistingParentPrompt(null)}
              className="border-[#333] text-white"
              data-testid="existing-parent-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleAddStudent({ addToExistingParent: true })}
              disabled={addingStudent}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="existing-parent-confirm"
            >
              {addingStudent ? 'Adding…' : 'Yes, add under existing account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentImport;
